import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { SignOut, X } from "@phosphor-icons/react";

// adminOnly = primary admin only · hideForSecondary = hidden for secondary_admin · designsOnly = admin + secondary_admin
const GROUPS = [
  { label: "Work", items: [
    { key: "home", label: "Home" },
    { key: "orders", label: "Orders", count: "orders" },
    { key: "gates", label: "Gates & Railings" },
    { key: "fabrication", label: "Fabrication" },
    { key: "delivery", label: "Delivery" },
    { key: "estimates", label: "Estimates" },
    { key: "tasks", label: "To-Do", count: "tasks" },
  ] },
  { label: "Money", items: [
    { key: "payments", label: "Payment In" },
    { key: "parties", label: "Parties" },
    { key: "dues", label: "Due Recovery", designsOnly: true },
    { key: "accounts", label: "Accounts" },
    { key: "daily_expenses", label: "Daily Expenses" },
    { key: "commissions", label: "Commissions" },
    { key: "welder_commissions", label: "Welder Comm." },
  ] },
  { label: "People", items: [
    { key: "welders", label: "Welders" },
    { key: "staff", label: "Staff Payroll" },
    { key: "operators", label: "Operators", adminOnly: true },
  ] },
  { label: "Library", items: [
    { key: "items", label: "Items" },
    { key: "designs", label: "Designs", designsOnly: true },
    { key: "reports", label: "Reports", adminOnly: true },
  ] },
  { label: "System", items: [
    { key: "theme", label: "Theme", designsOnly: true },
    { key: "recycle", label: "Recycle Bin", adminOnly: true },
    { key: "settings", label: "Settings", adminOnly: true },
  ] },
];

const allowed = (item, role) => {
  if (item.adminOnly && role !== "admin") return false;
  if (item.hideForSecondary && role === "secondary_admin") return false;
  if (item.designsOnly && !["admin", "secondary_admin"].includes(role)) return false;
  return true;
};

const initials = (n) => (n || "A").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

function useTaskBadge(role, active) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/tasks/summary");
        setN(["admin", "secondary_admin"].includes(role) ? (data?.total_pending || 0) : (data?.my_pending || 0));
      } catch (_e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [role, active]);
  return n;
}

function NavItem({ item, isActive, count, onSelect }) {
  return (
    <button onClick={() => onSelect(item.key)} data-testid={`nav-${item.key}`}
      className={"w-full flex items-center px-3 py-2 rounded-sm text-[15px] transition-colors " +
        (isActive ? "bg-slate-800 text-slate-50 font-medium shadow-sm border border-slate-700/70"
          : "text-slate-300 hover:bg-slate-800/60 hover:text-slate-100 border border-transparent")}>
      <span className="flex-1 text-left">{item.label}</span>
      {count > 0 && (
        <span className="text-xs text-slate-500 font-mono-num" data-testid={item.key === "tasks" ? "sidebar-tasks-badge" : `sidebar-count-${item.key}`}>
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

export default function Sidebar({ active, onSelect, user, onLogout, open, onClose, orderCount = 0 }) {
  const taskBadge = useTaskBadge(user?.role, active);
  const counts = { tasks: taskBadge, orders: orderCount };
  return (
    <>
      {open && <div onClick={onClose} className="lg:hidden fixed inset-0 bg-black/50 z-30" data-testid="sidebar-overlay" />}
      <aside data-testid="app-sidebar"
        className={"fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-slate-950 border-r border-slate-700/70 flex flex-col transition-transform duration-200 " +
          (open ? "translate-x-0" : "-translate-x-full lg:translate-x-0")}>
        <div className="px-5 pt-6 pb-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-orange-500 text-white font-bold flex items-center justify-center shrink-0" data-testid="brand-logo">S</div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="font-semibold text-slate-100 truncate">Shobhit Creation</div>
            <div className="text-xs text-slate-500">Factory OS</div>
          </div>
          <button onClick={onClose} className="lg:hidden text-slate-400 hover:text-slate-100" data-testid="sidebar-close"><X size={20} /></button>
        </div>
        <nav className="flex-1 px-3 pb-4 overflow-y-auto space-y-5">
          {GROUPS.map((g) => {
            const items = g.items.filter((it) => allowed(it, user?.role));
            if (!items.length) return null;
            return (
              <div key={g.label}>
                <div className="px-3 pb-1.5 text-xs text-slate-500">{g.label}</div>
                <div className="space-y-0.5">
                  {items.map((it) => (
                    <NavItem key={it.key} item={it} isActive={active === it.key} count={it.count ? counts[it.count] : 0} onSelect={onSelect} />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="mx-4 py-3 border-t border-slate-700/70 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-semibold text-slate-200">
            {initials(user?.name)}
          </div>
          <div className="flex-1 min-w-0 leading-tight">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name || "Admin"}</div>
            <div className="text-xs text-slate-500 capitalize">{(user?.role || "admin").replace(/_/g, " ")}</div>
          </div>
          <button onClick={onLogout} data-testid="sidebar-logout" className="text-slate-500 hover:text-red-400 p-1" title="Sign out">
            <SignOut size={16} />
          </button>
        </div>
      </aside>
    </>
  );
}
